import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { live } from '../data/live';
import type { AgentFloatAgent, AgentFloatRequest } from '../data/live';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

/**
 * Float: credit a counter buys before it can give any away.
 *
 * A counter takes cash from a driver and credits their wallet. Float is what
 * backs that credit — bought from the operator up front and spent as it is
 * issued, so a counter can never hand out more balance than it has paid for.
 * Before this existed, a counter could mint any amount.
 *
 * Two things live here: the queue of counters asking for more, and what each
 * one currently holds. Both read from the same ledger; nothing stores a total.
 */
export function AgentFloat() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'waiting' | 'all' | 'balances'>('waiting');
  const [deciding, setDeciding] = useState<AgentFloatRequest | null>(null);
  const [granting, setGranting] = useState<AgentFloatAgent | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['agent-float'],
    queryFn: () => live.agentFloat(),
    refetchInterval: 20_000,
  });

  const after = () => {
    void qc.invalidateQueries({ queryKey: ['agent-float'] });
    setDeciding(null);
    setGranting(null);
  };

  const decide = useMutation({
    mutationFn: (v: { id: string; approve: boolean; amountMinor?: number; reason?: string }) =>
      live.decideAgentFloat(v.id, v),
    onSuccess: after,
  });
  const grant = useMutation({
    mutationFn: (v: { id: string; amountMinor: number; memo?: string }) =>
      live.grantAgentFloat(v.id, v),
    onSuccess: after,
  });

  /* Both mutations outlive their dialogs, so a refusal from one request used
     to greet the next. Reset on every open — the same bug Top-ups had. */
  const openSettle = (r: AgentFloatRequest) => { decide.reset(); setDeciding(r); };
  const openAdjust = (a: AgentFloatAgent) => { grant.reset(); setGranting(a); };

  if (isLoading) return <Loading />;
  // A failed load is said, not spun on forever.
  if (!data) return <Empty title="Float is not available." hint={loadFailedHint(loadError)} />;

  const cur = data.currency;
  const money = (minor: number) =>
    `${cur} ${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const waiting = data.requests.filter((r) => r.state === 'awaiting_admin');
  const rows = tab === 'waiting' ? waiting : data.requests;
  /* A counter that cannot cover a single ordinary top-up is one that is about
     to turn a driver away at the counter, which is worth seeing before they
     do. */
  const empty = data.agents.filter((a) => a.floatMinor <= 0);

  return (
    <>
      <div className="tiles">
        <Tile k="Waiting" v={waiting.length}
              d={waiting.length ? 'counters asking for float' : 'queue clear'} />
        <Tile k="Float outstanding"
              v={money(data.agents.reduce((n, a) => n + a.floatMinor, 0))}
              d="credit bought and not yet issued" />
        <Tile k="Out of float" v={empty.length}
              d={empty.length ? 'cannot take a top-up' : 'all counters funded'} />
        <Tile k="Counters" v={data.agents.length} d="approved" />
      </div>

      <div className="chips" style={{ margin: '14px 0 10px' }}>
        <Chip label={`Waiting (${waiting.length})`} selected={tab === 'waiting'}
              onClick={() => setTab('waiting')} />
        <Chip label="All requests" selected={tab === 'all'} onClick={() => setTab('all')} />
        <Chip label={`Balances (${data.agents.length})`} selected={tab === 'balances'}
              onClick={() => setTab('balances')} />
      </div>

      {tab === 'balances' ? (
        data.agents.length === 0 ? (
          <Empty title="No approved counters" hint="Approve one on the Agents screen first." />
        ) : (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr><th>Counter</th><th>Area</th><th>Float</th><th /></tr>
              </thead>
              <tbody>
                {data.agents.map((a) => (
                  <tr key={a.id} className={a.floatMinor <= 0 ? 'off' : undefined}>
                    <td>{a.name}</td>
                    <td>{a.area}</td>
                    <td className="mono nowrap">
                      {money(a.floatMinor)}
                      {a.floatMinor <= 0 && <Pill tone="off">empty</Pill>}
                    </td>
                    <td className="act">
                      <button className="btn sm ghost" onClick={() => openAdjust(a)}>
                        Adjust
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : rows.length === 0 ? (
        <Empty
          title={tab === 'waiting' ? 'Nothing waiting' : 'No requests yet'}
          hint="A counter asks for float from the Spark Agent app."
        />
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Asked</th><th>Counter</th><th>Amount</th>
                <th>How they paid</th><th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="mono nowrap">{new Date(r.requestedAt).toLocaleString()}</td>
                  <td>{r.agentName}<div className="li-sub">{r.area}</div></td>
                  <td className="mono nowrap">
                    {money(r.amountMinor)}
                    {r.creditedMinor != null && r.creditedMinor !== r.amountMinor && (
                      <div className="li-sub">granted {money(r.creditedMinor)}</div>
                    )}
                  </td>
                  <td>{r.note ?? <span className="faint">—</span>}</td>
                  <td>
                    <Pill tone={r.state === 'approved' ? 'on' : r.state === 'awaiting_admin' ? 'mid' : 'off'}>
                      {r.state === 'awaiting_admin' ? 'Waiting' : r.state}
                    </Pill>
                    {r.rejectReason && <div className="li-sub">{r.rejectReason}</div>}
                    {r.settledBy && <div className="li-sub faint">by {r.settledBy}</div>}
                  </td>
                  <td className="act">
                    {r.state === 'awaiting_admin' && (
                      <button className="btn sm" onClick={() => openSettle(r)}>Settle</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note" style={{ marginTop: 12 }}>
        <b>Granting float is giving credit.</b> The counter can immediately hand
        that much balance to drivers, so grant it once the payment has actually
        arrived. Taking it back is possible — a negative adjustment — but the
        credit it has already issued cannot be recalled.
      </div>

      {deciding && (
        <Settle
          request={deciding}
          currency={cur}
          pending={decide.isPending}
          error={decide.error ? errorText(decide.error) : null}
          onClose={() => setDeciding(null)}
          onDecide={(v) => decide.mutate({ id: deciding.id, ...v })}
        />
      )}
      {granting && (
        <Adjust
          agent={granting}
          currency={cur}
          pending={grant.isPending}
          error={grant.error ? errorText(grant.error) : null}
          onClose={() => setGranting(null)}
          onGrant={(v) => grant.mutate({ id: granting.id, ...v })}
        />
      )}
    </>
  );
}

function Dialog({ label, children, onClose }: {
  label: string; children: React.ReactNode; onClose: () => void;
}) {
  return (
    <div
      role="dialog" aria-label={label}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420 }}>{children}</div>
    </div>
  );
}

function Settle({ request, currency, pending, error, onClose, onDecide }: {
  request: AgentFloatRequest;
  currency: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onDecide: (v: { approve: boolean; amountMinor?: number; reason?: string }) => void;
}) {
  /* Editable, and pre-filled with what was asked: what arrived is what
     arrived, not what was typed into the app. */
  const [amount, setAmount] = useState((request.amountMinor / 100).toString());
  const [reason, setReason] = useState('');
  const n = Number(amount);
  const bad = !(n > 0) ? 'Enter an amount greater than zero.' : null;

  return (
    <Dialog label="Settle float request" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>
        {request.agentName} · {currency} {(request.amountMinor / 100).toFixed(2)}
      </div>
      <div className="li-sub" style={{ marginBottom: 12 }}>
        Asked {new Date(request.requestedAt).toLocaleString()}
        {request.note ? ` · ${request.note}` : ''}
      </div>

      <label className="li-sub" htmlFor="fl-amount">Float to grant</label>
      <input id="fl-amount" className="field mono" value={amount} inputMode="decimal"
             onChange={(e) => setAmount(e.target.value)} />

      <label className="li-sub" style={{ marginTop: 10 }} htmlFor="fl-reason">
        Reason, if refusing
      </label>
      <input id="fl-reason" className="field" value={reason}
             placeholder="No payment received" onChange={(e) => setReason(e.target.value)} />

      {(bad || error) && (
        <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 10 }}>
          {error ?? bad}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                onClick={() => onDecide({ approve: true, amountMinor: Math.round(n * 100) })}>
          {pending ? 'Working…' : 'Grant float'}
        </button>
        <button className="btn ghost" style={{ flex: 1 }} disabled={pending}
                onClick={() => onDecide({ approve: false, reason })}>
          Refuse
        </button>
      </div>
      <button className="btn ghost sm" style={{ width: '100%', marginTop: 8 }}
              onClick={onClose}>Cancel</button>
    </Dialog>
  );
}

function Adjust({ agent, currency, pending, error, onClose, onGrant }: {
  agent: AgentFloatAgent;
  currency: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onGrant: (v: { amountMinor: number; memo?: string }) => void;
}) {
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const n = Number(amount);
  const bad = amount.trim() === '' || !Number.isFinite(n) || n === 0
    ? 'Enter an amount. Negative takes float back.'
    : null;

  return (
    <Dialog label="Adjust float" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>{agent.name}</div>
      <div className="li-sub" style={{ marginBottom: 12 }}>
        Holds {currency} {(agent.floatMinor / 100).toFixed(2)} now.
      </div>

      <label className="li-sub" htmlFor="adj-amount">Change by</label>
      <input id="adj-amount" className="field mono" value={amount} inputMode="decimal"
             placeholder="5000, or -5000 to take it back"
             onChange={(e) => setAmount(e.target.value)} />
      <div className="li-sub faint" style={{ marginTop: 4, marginBottom: 10 }}>
        Written as a ledger entry, so the history stays intact. There is no "set
        balance": a total that can be overwritten is a total nobody can audit.
      </div>

      <label className="li-sub" htmlFor="adj-memo">Note</label>
      <input id="adj-memo" className="field" value={memo}
             placeholder="Bank transfer 24 Sep" onChange={(e) => setMemo(e.target.value)} />

      {(bad || error) && (
        <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 10 }}>
          {error ?? bad}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                onClick={() => onGrant({ amountMinor: Math.round(n * 100), memo })}>
          {pending ? 'Working…' : n < 0 ? 'Take float back' : 'Grant float'}
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Dialog>
  );
}
